import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import Icon from '../ui/Icon';
import { CourierBadge, StatusPill, Toggle, PushToast } from '../ui/primitives';
import { courierOfOrder, formatDeliverySlot, fmtMoney } from '../ui/couriers';
import DeliveryMap from './DeliveryMap';
import { LiveFeed } from './DashboardPage';
import {
  useDashboardStats, useActiveSessions, usePackingHistory, useAdminOrders, useDeliveries, useTrackedParcels, useLiveTracking, useLiveFeed, useSenderHub, AdminOrder,
} from './hooks';
import { API_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import MagazynBoard from '../magazyn/MagazynBoard';
import { useStockConfig } from '../magazyn/api';
import { useAdminStockPrint } from './magazyn/shared';

/* PackFlow Admin Mobile — wariant telefonowy panelu (dolna nawigacja). */

function MHome({ orgLabel }: { orgLabel: string }) {
  const { data: stats } = useDashboardStats();
  const { data: sessions = [] } = useActiveSessions();
  const { data: history = [] } = usePackingHistory();
  const { data: orders = [] } = useAdminOrders();
  const deliveries = useDeliveries(orders, sessions);
  const hub = useSenderHub();
  const parcels = useLiveTracking(useTrackedParcels(orders, sessions, hub));
  const feed = useLiveFeed(sessions, history);
  const { user } = useAuth();
  const firstName = user?.email?.split('@')[0]?.split(/[._-]/)[0] || 'Admin';

  const kpis: [string, string, React.ReactNode][] = [
    ['clock', 'Oczekujące', stats?.pendingOrders ?? '—'],
    ['package-check', 'Spakowane', stats?.packedToday ?? '—'],
    ['timer', 'Śr. czas', stats?.avgPackingTime ?? '—'],
    ['tag', 'Etykiety', stats?.labelsToday ?? '—'],
  ];

  return (
    <div className="m-scroll">
      <div className="m-hero">
        <div>
          <div className="m-hi">Cześć, {firstName.charAt(0).toUpperCase() + firstName.slice(1)}</div>
          <div className="m-sub">{orgLabel}</div>
        </div>
        <span className="pill pill-ok"><Icon name="circle" size={9} /> live</span>
      </div>

      <div className="m-kpis">
        {kpis.map(([ic, lbl, val]) => (
          <div className="m-kpi" key={lbl}>
            <div className="mk-lbl"><Icon name={ic} size={13} /> {lbl}</div>
            <div className="mk-val">{val}</div>
          </div>
        ))}
      </div>

      <DeliveryMap deliveries={deliveries} parcels={parcels} packedToday={stats?.packedToday} hub={hub} />

      <div className="m-sec">Aktywność na żywo</div>
      <div className="card"><LiveFeed feed={feed.slice(0, 6)} /></div>
    </div>
  );
}

function MOrders({ push }: { push: PushToast }) {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [sel, setSel] = useState<AdminOrder | null>(null);
  const { data: orders = [] } = useAdminOrders();

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: orders.length };
    orders.forEach((o) => {
      const k = courierOfOrder(o);
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [orders]);

  const list = orders.filter((o) => {
    const okF = filter === 'all' || courierOfOrder(o) === filter;
    const name = `${o.customer?.first_name || ''} ${o.customer?.last_name || ''}`.toLowerCase();
    const okQ = !q || o.name.toLowerCase().includes(q.toLowerCase()) || name.includes(q.toLowerCase());
    return okF && okQ;
  });

  const fdefs: [string, string][] = [['all', 'Wszystkie'], ['inpost', 'InPost'], ['dhl', 'DHL'], ['goodspeed', 'GoodSpeed'], ['cod', 'COD'], ['paczkomat', 'Paczkomat']];

  async function printDoc(orderId: number, kind: 'list' | 'labels' | 'all') {
    try {
      if (kind === 'list' || kind === 'all') await axios.post(`${API_URL}/api/printing/print-packing-list`, { orderId });
      if (kind === 'labels' || kind === 'all') await axios.post(`${API_URL}/api/printing/print-product-labels`, { orderId });
      push(kind === 'all' ? 'Komplet dokumentów → druk' : kind === 'list' ? 'List pakowania → druk' : 'Etykiety → druk');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd druku', 'err'); }
  }

  return (
    <div className="m-scroll">
      <div className="m-pagehead">Zamówienia</div>
      <div className="m-search">
        <Icon name="search" size={16} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj #numer / klient…" />
      </div>
      <div className="m-chips">
        {fdefs.map(([id, lbl]) => (
          <button key={id} className="chip" aria-pressed={filter === id} onClick={() => setFilter(id)}>{lbl}<span className="c">{counts[id] || 0}</span></button>
        ))}
      </div>
      <div className="m-orders">
        {list.map((o) => (
          <button className="m-order" key={o.id} onClick={() => setSel(o)}>
            <div className="mo-top">
              <span className="mono mo-num">{o.name}</span>
              <CourierBadge courier={courierOfOrder(o)} />
            </div>
            <div className="mo-cust">{o.customer?.first_name} {o.customer?.last_name}</div>
            <div className="mo-meta">{o.shipping_address?.city || '—'} · {o.line_items?.reduce((s, i) => s + i.quantity, 0) ?? '—'} poz. · {fmtMoney(o.total_price, o.currency)}</div>
            <div className="mo-foot"><StatusPill status="pending" /><span className="mono muted" style={{ fontSize: 11 }}>{formatDeliverySlot(o)}</span></div>
          </button>
        ))}
        {list.length === 0 && <div className="empty" style={{ padding: '40px 16px' }}><div className="ei"><Icon name="search-x" size={26} /></div><h3>Brak wyników</h3></div>}
      </div>

      {sel && (
        <div className="m-sheet-scrim" onClick={() => setSel(null)}>
          <div className="m-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="m-grab" />
            <div className="flex" style={{ marginBottom: 12 }}>
              <span className="mono" style={{ fontSize: 22, fontWeight: 700 }}>{sel.name}</span>
              <span className="right"><CourierBadge courier={courierOfOrder(sel)} /></span>
            </div>
            <dl className="dl-grid" style={{ gridTemplateColumns: '110px 1fr', marginBottom: 16 }}>
              <dt>Klient</dt><dd>{sel.customer?.first_name} {sel.customer?.last_name}</dd>
              <dt>Miasto</dt><dd>{sel.shipping_address?.city || '—'}</dd>
              <dt>Wartość</dt><dd className="num">{fmtMoney(sel.total_price, sel.currency)}</dd>
              <dt>Dostawa</dt><dd className="mono">{formatDeliverySlot(sel)}</dd>
              <dt>Data</dt><dd className="mono">{new Date(sel.created_at).toLocaleDateString('pl-PL')}</dd>
            </dl>
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button className="btn btn-ghost" style={{ justifyContent: 'center', minHeight: 46 }} onClick={() => printDoc(sel.id, 'list')}><Icon name="file-text" size={16} /> List pak.</button>
              <button className="btn btn-ghost" style={{ justifyContent: 'center', minHeight: 46 }} onClick={() => printDoc(sel.id, 'labels')}><Icon name="tag" size={16} /> Etykiety</button>
            </div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 10, minHeight: 48 }} onClick={() => { printDoc(sel.id, 'all'); setSel(null); }}>
              <Icon name="printer" size={16} /> Drukuj komplet
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MPrints({ push }: { push: PushToast }) {
  const { data: printers = [] } = useQuery<any[]>({
    queryKey: ['settings-printers'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/printers`)).data,
  });
  const { data: reports } = useQuery<{ history: any[] }>({
    queryKey: ['report-history'],
    queryFn: async () => (await axios.get(`${API_URL}/api/printing/report-history?limit=10`)).data,
  });

  return (
    <div className="m-scroll">
      <div className="m-pagehead">Druk · realtime</div>
      <div className="m-sec" style={{ marginTop: 4 }}>Drukarki</div>
      <div className="card" style={{ marginBottom: 16 }}>
        {printers.map((p: any) => (
          <div className="feed-row" key={p.id || p.name}>
            <div className="ic" style={{ background: p.is_active !== false ? 'var(--success-soft)' : 'var(--danger-soft)', color: p.is_active !== false ? 'var(--success-text)' : 'var(--danger-text)' }}><Icon name="printer" size={15} /></div>
            <div className="t" style={{ minWidth: 0 }}><b>{p.name}</b><div className="muted mono" style={{ fontSize: 11 }}>{p.ip ? `${p.ip}:${p.port || 9100}` : '—'} · {p.protocol || 'RAW'}</div></div>
            {p.is_active !== false ? <span className="pill pill-ok" style={{ marginLeft: 'auto' }}>OK</span> : <span className="pill pill-err" style={{ marginLeft: 'auto' }}>Wył.</span>}
          </div>
        ))}
        {printers.length === 0 && <div className="empty" style={{ padding: '26px 14px' }}><div className="ei"><Icon name="printer" size={24} /></div><h3>Brak drukarek</h3></div>}
      </div>
      <div className="m-sec">Ostatnie raporty</div>
      <div className="card">
        {(reports?.history || []).map((j: any) => (
          <div className="feed-row" key={j.id}>
            <div className="ic" style={{ background: 'var(--surface-3)', color: 'var(--fg-2)' }}><Icon name="file-text" size={15} /></div>
            <div className="t" style={{ minWidth: 0 }}><b>{j.report_type || 'Raport produktowy'}</b><div className="muted" style={{ fontSize: 11 }}>{new Date(j.created_at).toLocaleString('pl-PL')}</div></div>
            <span className="pill pill-ok" style={{ marginLeft: 'auto' }}><Icon name="check" size={12} /> OK</span>
          </div>
        ))}
        {!(reports?.history || []).length && <div className="empty" style={{ padding: '26px 14px' }}><div className="ei"><Icon name="file-bar-chart" size={24} /></div><h3>Brak raportów</h3></div>}
      </div>
    </div>
  );
}

function MMore({ theme, setTheme, push, orgLabel, connected }: { theme: string; setTheme: (t: string) => void; push: PushToast; orgLabel: string; connected: boolean }) {
  const { signOut } = useAuth();
  const { data: users = [] } = useQuery<any[]>({
    queryKey: ['settings-users'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/users`)).data,
  });
  const { data: stations = [] } = useQuery<any[]>({
    queryKey: ['workstations'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/workstations`)).data,
  });

  return (
    <div className="m-scroll">
      <div className="m-pagehead">Więcej</div>
      <div className="card" style={{ padding: '4px 16px', marginBottom: 16 }}>
        <div className="set-row">
          <div className="meta"><div className="t">Motyw ciemny</div><div className="d">Kiosk / noc</div></div>
          <Toggle on={theme === 'dark'} onChange={(v) => setTheme(v ? 'dark' : 'light')} />
        </div>
        <div className="set-row">
          <div className="meta"><div className="t">Agent on-prem</div><div className="d">{connected ? 'połączony' : 'offline (kolejka)'}</div></div>
          {connected
            ? <span className="pill pill-ok"><Icon name="check" size={12} /> OK</span>
            : <span className="pill pill-warn"><Icon name="cloud-off" size={12} /> offline</span>}
        </div>
      </div>
      <div className="m-sec">Organizacja</div>
      <div className="card" style={{ padding: '4px 16px' }}>
        {([
          ['building-2', orgLabel, 'tenant'],
          ['users', `${users.length} operatorów`, 'PIN, role'],
          ['cpu', `${stations.length} stacji`, stations.map((s: any) => s.location).filter(Boolean).slice(0, 2).join(' · ') || 'stacje robocze'],
          ['orbit', 'Universal Carrier Service', 'kurierzy + Shopify · platforma'],
        ] as [string, string, string][]).map(([ic, t, d]) => (
          <div className="set-row" key={t} style={{ cursor: 'pointer' }} onClick={() => push('Pełna konfiguracja w panelu desktop', 'info')}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--accent-soft)', color: 'var(--accent-text)', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name={ic} size={17} /></div>
            <div className="meta"><div className="t">{t}</div><div className="d">{d}</div></div>
            <Icon name="chevron-right" size={16} style={{ color: 'var(--fg-3)' }} />
          </div>
        ))}
      </div>
      <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center', marginTop: 16, minHeight: 46 }} onClick={() => signOut()}>
        <Icon name="log-out" size={16} /> Wyloguj
      </button>
      <div className="muted" style={{ textAlign: 'center', fontSize: 11, padding: '18px 0' }}>
        PackFlow Mobile · moduł Insolitum Universe<br />Pełna administracja dostępna w panelu desktop
      </div>
    </div>
  );
}

/* Magazyn na telefonie — „schodzę do piekarni z telefonem": ta sama tablica co w kiosku, jedną ręką. */
function MStock({ push }: { push: PushToast }) {
  const onPrint = useAdminStockPrint(push);
  return (
    <div className="m-scroll">
      {/* Godzina resetu i zakres pokazuje sama tablica — tu tylko tytuł, żeby zostało miejsce na kafelki. */}
      <div className="m-pagehead">Magazyn</div>
      <MagazynBoard variant="admin" compact allowPdf={false} onPrint={onPrint} notify={push} />
    </div>
  );
}

type Props = { theme: string; setTheme: (t: string) => void; orgLabel: string; push: PushToast; connected: boolean };

export default function MobileAdmin({ theme, setTheme, orgLabel, push, connected }: Props) {
  const [tab, setTab] = useState('home');
  const [now, setNow] = useState(() => new Date());
  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  // Magazyn tylko, gdy moduł włączony; po wyłączeniu wracamy na pulpit.
  const { data: stockConfig } = useStockConfig();
  const stockOn = !!stockConfig?.enabled;
  React.useEffect(() => { if (!stockOn && tab === 'stock') setTab('home'); }, [stockOn, tab]);
  const tabs: [string, string, string][] = [
    ['home', 'Pulpit', 'layout-dashboard'],
    ['orders', 'Zamówienia', 'list'],
    ...(stockOn ? [['stock', 'Magazyn', 'warehouse'] as [string, string, string]] : []),
    ['prints', 'Druk', 'printer'],
    ['more', 'Więcej', 'menu'],
  ];

  return (
    <div className="m-app" data-theme={theme}>
      <div className="m-statusbar">
        <span style={{ fontWeight: 700 }}>PackFlow</span>
        <span className="muted">· {orgLabel}</span>
        <span className="right mono">{String(now.getHours()).padStart(2, '0')}:{String(now.getMinutes()).padStart(2, '0')}</span>
      </div>
      <div className="m-body">
        {tab === 'home' && <MHome orgLabel={orgLabel} />}
        {tab === 'orders' && <MOrders push={push} />}
        {tab === 'stock' && stockOn && <MStock push={push} />}
        {tab === 'prints' && <MPrints push={push} />}
        {tab === 'more' && <MMore theme={theme} setTheme={setTheme} push={push} orgLabel={orgLabel} connected={connected} />}
      </div>
      <div className="m-nav">
        {tabs.map(([id, lbl, ic]) => (
          <button key={id} className={`m-tab ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>
            <Icon name={ic} size={21} />
            <span>{lbl}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
